import { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import MainLayout from 'components/layout/MainLayout';
import {
  FileText, Users, Building2, Calendar,
  CheckCircle2, Trash2, Send, Plus, Loader2, X, Edit, ArrowLeft,
  Upload, PackageOpen, ChevronDown, ChevronUp, Sparkles, ShieldAlert, Check,
  Settings, Video, Clock 
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

/* ─── GLOBAL STYLES (Cyberpunk Aesthetic) ────────────────────────────────── */
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
  
  .cyber-modules-root {
    font-family: 'DM Sans', sans-serif;
    color: #ffffff;
    animation: fadeIn 0.4s ease;
  }

  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  .glass-panel {
    background: rgba(10, 8, 30, 0.65);
    backdrop-filter: blur(24px) saturate(160%);
    -webkit-backdrop-filter: blur(24px) saturate(160%);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 20px;
    box-shadow: 0 4px 32px rgba(0, 0, 0, 0.4), inset 0 0 0 0.5px rgba(255,255,255,0.05);
  }
  
  .title-neon {
    color: #FFF;
    text-shadow: 0 0 15px rgba(0, 255, 255, 0.4);
    letter-spacing: -0.02em;
  }

  /* ── GLOW EFFECTS ── */
  .glow-cyan { box-shadow: 0 0 20px rgba(0, 255, 255, 0.08); border-color: rgba(0, 255, 255, 0.2); }
  .glow-magenta { box-shadow: 0 0 20px rgba(255, 0, 255, 0.08); border-color: rgba(255, 0, 255, 0.2); }
  
  /* ── NEON BUTTONS ── */
  .btn-neon-cyan {
    background: #00FFFF; color: #000; font-weight: 800; border: none;
    box-shadow: 0 0 15px rgba(0, 255, 255, 0.4); transition: all 0.2s;
  }
  .btn-neon-cyan:hover:not(:disabled) { background: #66FFFF; box-shadow: 0 0 25px rgba(0, 255, 255, 0.6); transform: translateY(-1px); }
  .btn-neon-cyan:disabled { background: rgba(0, 255, 255, 0.1); color: rgba(0, 255, 255, 0.3); box-shadow: none; cursor: not-allowed; }

  .btn-neon-magenta {
    background: #FF00FF; color: #fff; font-weight: 800; border: none;
    box-shadow: 0 0 15px rgba(255, 0, 255, 0.4); transition: all 0.2s;
  }
  .btn-neon-magenta:hover:not(:disabled) { background: #ff4dff; box-shadow: 0 0 25px rgba(255, 0, 255, 0.6); transform: translateY(-1px); }
  .btn-neon-magenta:disabled { background: rgba(255, 0, 255, 0.1); color: rgba(255, 255, 255, 0.3); box-shadow: none; cursor: not-allowed; }

  .btn-neon-lime {
    background: #ccff00; color: #000; font-weight: 800; border: none;
    box-shadow: 0 0 15px rgba(204, 255, 0, 0.4); transition: all 0.2s;
  }
  .btn-neon-lime:hover:not(:disabled) { background: #d4ff33; box-shadow: 0 0 25px rgba(204, 255, 0, 0.6); transform: translateY(-1px); }
  .btn-neon-lime:disabled { background: rgba(204, 255, 0, 0.1); color: rgba(204, 255, 0, 0.3); box-shadow: none; cursor: not-allowed; }

  /* ── INPUTS ── */
  .cy-input {
    width: 100%; padding: 12px 16px; border-radius: 12px;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1);
    color: #fff; font-size: 13px; transition: all 0.3s;
  }
  .cy-input:focus { border-color: #00FFFF; box-shadow: 0 0 15px rgba(0,255,255,0.2); outline: none; }
  .cy-input::placeholder { color: rgba(255,255,255,0.3); }
  
  /* Select styling */
  select.cy-input option { background: #060810; color: #fff; }

  /* Custom Scrollbar */
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: rgba(255,255,255,0.02); }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 4px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(0,255,255,0.4); }
    /* ── FIX OVERFLOW ── */
  .cyber-modules-root,
  .cyber-modules-root * {
    box-sizing: border-box;
  }

  .glass-panel {
    overflow: hidden;
    width: 100%;
  }

  .module-row {
    overflow: hidden;
    width: 100%;
    min-width: 0;
  }

  .module-row-badges {
    overflow: hidden;
    min-width: 0;
  }

  .module-row-meta {
    min-width: 0;
    overflow: hidden;
  }

  .module-row-actions {
    flex-shrink: 0;
  }
`;

const SuperAdminModules = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgFromUrl = searchParams.get('org') || '';

  const [allModules, setAllModules]               = useState([]);
  const [orgModules, setOrgModules]               = useState([]);
  const [loadingOrgModules, setLoadingOrgModules] = useState(false);
  const [organizations, setOrganizations]         = useState([]);
  const [departments, setDepartments]             = useState([]);
  const [users, setUsers]                         = useState([]);
  const [loading, setLoading]                     = useState(true);
  const [deletingId, setDeletingId]               = useState(null);

  const [view, setView]               = useState(orgFromUrl ? 'assigned' : 'all');
  const [filterOrg, setFilterOrg]     = useState(orgFromUrl);
  const [filterOrgName, setFilterOrgName] = useState('');

  // ── Upload ZIP con unidades ───────────────────────────────────────────────
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [uploadingUnits, setUploadingUnits]   = useState(false);
  const [unitsFile, setUnitsFile]             = useState(null);
  const [uploadResult, setUploadResult]       = useState(null);

  // Modal asignación
  const [selectedModule, setSelectedModule]   = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedOrg, setSelectedOrg]         = useState(orgFromUrl);
  const [assigning, setAssigning]             = useState(false);
  const [assignmentData, setAssignmentData]   = useState({
    assignTo: 'all', departmentIds: [], userIds: [], dueDate: '', mandatory: true,
  });

  // Modal de Configuración (Grabación y Horas)
  const [configModule, setConfigModule]       = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configuring, setConfiguring]         = useState(false);
  const [configData, setConfigData]           = useState({ requires_screen_record: false, hours: 0 });

  // ── Carga inicial ──────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [modsRes, orgsRes] = await Promise.all([
          api.get('/superadmin/all-modules'),
          api.get('/superadmin/organizations'),
        ]);
        setAllModules(modsRes.data);
        setOrganizations(orgsRes.data);

        if (orgFromUrl) {
          const org = orgsRes.data.find(o => o.id === orgFromUrl);
          if (org) setFilterOrgName(org.name);
          await fetchOrgModules(orgFromUrl);
        }
      } catch {
        toast.error('Anomalía en la red: Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // ── Cargar módulos asignados a una org ─────────────────────────────────────
  const fetchOrgModules = async (orgId) => {
    if (!orgId) { setOrgModules([]); return; }
    setLoadingOrgModules(true);
    try {
      const res = await api.get(`/superadmin/organizations/${orgId}/modules`);
      setOrgModules(res.data);
    } catch {
      setOrgModules([]);
    } finally {
      setLoadingOrgModules(false);
    }
  };

  // Departamentos / usuarios al cambiar org del modal
  useEffect(() => {
    if (selectedOrg) {
      api.get(`/admin/departments?organization_id=${selectedOrg}`)
        .then(r => setDepartments(r.data)).catch(() => setDepartments([]));
      api.get(`/admin/users?organization_id=${selectedOrg}`)
        .then(r => setUsers(r.data)).catch(() => setUsers([]));
    } else {
      setDepartments([]); setUsers([]);
    }
  }, [selectedOrg]);

  const displayedModules = view === 'assigned' && filterOrg ? orgModules : allModules;
  const assignedIds      = new Set(orgModules.map(m => m.id));

  // ── Eliminar ───────────────────────────────────────────────────────────────
  const deleteModule = async (moduleId, title) => {
    if (!confirm(`¿Confirmar purga de datos: "${title}"?`)) return;
    setDeletingId(moduleId);
    try {
      await api.delete(`/admin/modules/${moduleId}`);
      setAllModules(prev => prev.filter(m => m.id !== moduleId));
      setOrgModules(prev => prev.filter(m => m.id !== moduleId));
      toast.success('Bloque de datos purgado con éxito');
    } catch (err) {
      toast.error('Error de purga: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  // ── Guardar Configuración (Horas / Grabación) ─────────────────────────────
  const openConfig = (module) => {
    setConfigModule(module);
    setConfigData({
      requires_screen_record: module.requires_screen_record || false,
      hours: module.hours || 0
    });
    setShowConfigModal(true);
  };
  const [showRenameModal, setShowRenameModal] = useState(false);
const [renameTarget, setRenameTarget] = useState({ id: null, title: '', type: 'module' });
const [renameValue, setRenameValue] = useState('');
const [renameSaving, setRenameSaving] = useState(false);

const openRename = (id, title, type = 'module') => {
  setRenameTarget({ id, title, type });
  setRenameValue(title);
  setShowRenameModal(true);
};

const saveRename = async () => {
  if (!renameValue.trim()) return;
  setRenameSaving(true);
  try {
    const endpoint = renameTarget.type === 'module'
      ? `/admin/modules/${renameTarget.id}`
      : `/admin/modules/units/${renameTarget.id}`;
    await api.patch(endpoint, { title: renameValue.trim() });
    toast.success('Nombre actualizado');
    setShowRenameModal(false);
    // Actualiza estado local sin refetch
    setAllModules(prev => prev.map(m =>
      m.id === renameTarget.id ? { ...m, title: renameValue.trim() } : m
    ));
    setOrgModules(prev => prev.map(m =>
      m.id === renameTarget.id ? { ...m, title: renameValue.trim() } : m
    ));
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error al renombrar');
  } finally {
    setRenameSaving(false);
  }
};

  const closeConfigModal = () => {
    setShowConfigModal(false);
    setConfigModule(null);
  };

  const submitConfig = async () => {
    setConfiguring(true);
    try {
      // Formateamos el payload para que coincida con lo que espera Python
      const payload = {
        require_screen_recording: configData.requires_screen_record,
        hours: configData.hours
      };

      // USAMOS LA RUTA CORRECTA Y EL MÉTODO PUT
      await api.put(`/superadmin/modules/${configModule.id}/settings`, payload);
      
      toast.success(`Configuración actualizada para "${configModule.title}"`);
      
      // Actualizamos el estado local
      setAllModules(prev => prev.map(m => m.id === configModule.id ? { ...m, ...configData } : m));
      setOrgModules(prev => prev.map(m => m.id === configModule.id ? { ...m, ...configData } : m));
      
      closeConfigModal();
    } catch (err) {
      toast.error('Error al actualizar configuración: ' + (err.response?.data?.detail || err.message));
    } finally {
      setConfiguring(false);
    }
  };

  // ── Upload ZIP con unidades ───────────────────────────────────────────────
  const uploadScormWithUnits = async () => {
    if (!unitsFile) { toast.error('Requiere inyección de archivo ZIP'); return; }
    setUploadingUnits(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', unitsFile);
      const res = await api.post('/superadmin/upload-scorm-with-units', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(res.data);
      toast.success(
        `✅ Estructura "${res.data.parent_module_title}" compilada — ${res.data.submodules_created} Nodos · ${res.data.tests_created} Evaluaciones`
      );
      setUnitsFile(null);
      // Recargar lista de módulos
      const modsRes = await api.get('/superadmin/all-modules');
      setAllModules(modsRes.data);
      if (filterOrg) await fetchOrgModules(filterOrg);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fallo crítico en compilación ZIP');
    } finally {
      setUploadingUnits(false);
    }
  };

  // ── Modal asignación ───────────────────────────────────────────────────────
  const handleAssignModule = (module) => {
    setSelectedModule(module);
    setSelectedOrg(filterOrg || '');
    setAssignmentData({ assignTo: 'all', departmentIds: [], userIds: [], dueDate: '', mandatory: true });
    setShowAssignModal(true);
  };

  const closeModal = () => { setShowAssignModal(false); setSelectedModule(null); };

  const submitAssignment = async () => {
    if (!selectedOrg) { toast.error('Designa una corporación destino'); return; }
    setAssigning(true);
    try {
      await api.post('/admin/assignments', {
        module_id:       selectedModule.id,
        organization_id: selectedOrg,
        assign_to:       assignmentData.assignTo,
        department_ids:  assignmentData.departmentIds,
        user_ids:        assignmentData.userIds,
        due_date:        assignmentData.dueDate || null,
        mandatory:       assignmentData.mandatory,
      });
      toast.success(`Carga "${selectedModule.title}" distribuida correctamente ✓`);
      if (filterOrg) await fetchOrgModules(filterOrg);
      closeModal();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      if (err.response?.status === 409) {
        toast.info(detail);
        if (filterOrg) await fetchOrgModules(filterOrg);
        closeModal();
      } else {
        toast.error('Error de distribución: ' + detail);
      }
    } finally {
      setAssigning(false);
    }
  };

  const toggleId = (field, id) => {
    setAssignmentData(prev => ({
      ...prev,
      [field]: prev[field].includes(id)
        ? prev[field].filter(x => x !== id)
        : [...prev[field], id],
    }));
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <MainLayout>
        <style>{GLOBAL_STYLES}</style>
        <div className="cyber-modules-root flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <Loader2 className="w-12 h-12 animate-spin text-[#00FFFF] drop-shadow-[0_0_15px_#00FFFF]" />
          <p className="text-[10px] font-black uppercase tracking-widest text-[#00FFFF]">Extrayendo Nodos de Conocimiento...</p>
        </div>
      </MainLayout>
    );
  }

return (
  <MainLayout>
    <style>{GLOBAL_STYLES}</style>
    <div className="cyber-modules-root"
      style={{ 
        padding: '32px 24px',
        boxSizing: 'border-box',
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-start'
      }}>
      <div style={{
        width: '100%',
        maxWidth: '1100px',
        margin: '0',
        padding: '0'
      }}>

        {/* ── Header ── */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-start gap-10 mb-8">
          <div className="min-w-0">
            {filterOrg && (
              <button onClick={() => navigate('/superadmin')} className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-[#00FFFF] hover:text-white mb-4 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Volver a Control Central
              </button>
            )}
            <h1 className="text-3xl md:text-4xl font-black title-neon tracking-tight mb-2 truncate" style={{ fontFamily: 'Roberta, serif' }}>
              Gestión de Módulos
            </h1>
            {filterOrgName ? (
              <p className="text-white/60 text-[11px] font-bold uppercase tracking-widest flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Entorno: {filterOrgName}
              </p>
            ) : (
              <p className="text-white/60 text-[11px] font-bold uppercase tracking-widest">Base de Conocimiento Global</p>
            )}
          </div>
          
          <button
            onClick={() => { setShowUploadPanel(p => !p); setUploadResult(null); }}
            className={`flex items-center gap-2 px-6 py-3.5 rounded-xl font-black text-[11px] uppercase tracking-widest transition-all flex-shrink-0 ${
              showUploadPanel ? 'bg-[#FF00FF]/20 text-[#FF00FF] border border-[#FF00FF]/40' : 'btn-neon-magenta'
            }`}
          >
            <PackageOpen className="w-4 h-4" />
            Compilar SCORM
            {showUploadPanel ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </button>
        </div>

        {/* ── Panel subida ZIP con unidades ── */}
        {showUploadPanel && (
          <div className="glass-panel border-[#FF00FF]/30 glow-magenta overflow-hidden mb-8">
            <div className="flex items-center gap-4 px-8 py-5 bg-gradient-to-r from-[#FF00FF]/15 to-transparent border-b border-[#FF00FF]/20">
              <div className="p-2.5 bg-[#FF00FF]/20 rounded-lg border border-[#FF00FF]/40 text-[#FF00FF]">
                <PackageOpen className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h2 className="font-black text-white text-lg tracking-tight truncate">Inyección de Paquete SCORM</h2>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#FF00FF] mt-0.5 truncate">
                  Procesamiento Automatizado de Nodos y Evaluaciones
                </p>
              </div>
            </div>

            <div className="p-8 space-y-6">
              {/* Instrucciones */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                {[
                  { step: '1', text: 'Prepara el ZIP Maestro conteniendo los ZIPs individuales por unidad y los PDFs de respuestas.' },
                  { step: '2', text: 'El motor creará la jerarquía: Módulo Padre → Sub-Módulos → Evaluaciones de forma autónoma.' },
                  { step: '3', text: 'Los cuestionarios se extraerán del PDF oficial. En su ausencia, intervendrá la IA Generativa.' },
                ].map(({ step, text }) => (
                  <div key={step} className="flex gap-3 p-4 bg-black/40 border border-white/5 rounded-xl">
                    <span className="flex-shrink-0 w-6 h-6 rounded-md bg-[#FF00FF]/20 text-[#FF00FF] border border-[#FF00FF]/30 text-xs font-black flex items-center justify-center">{step}</span>
                    <p className="text-white/60 leading-relaxed font-medium">{text}</p>
                  </div>
                ))}
              </div>

              {/* Input + botón */}
              <div className="flex flex-col lg:flex-row gap-4 items-stretch">
                <label className="flex-1 flex items-center gap-4 px-6 py-4 border-2 border-dashed border-[#FF00FF]/30 bg-[#FF00FF]/5 rounded-xl cursor-pointer hover:border-[#FF00FF] hover:bg-[#FF00FF]/10 transition-all group min-w-0">
                  <div className="p-3 bg-[#FF00FF]/10 rounded-lg text-[#FF00FF] group-hover:scale-110 transition-transform flex-shrink-0">
                    <Upload className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    {unitsFile ? (
                      <>
                        <p className="text-[14px] font-bold text-white truncate mb-0.5">{unitsFile.name}</p>
                        <p className="text-[10px] uppercase font-black tracking-widest text-[#FF00FF]">{(unitsFile.size / 1024 / 1024).toFixed(2)} MB Listos</p>
                      </>
                    ) : (
                      <>
                        <p className="text-[13px] font-bold text-white mb-0.5">Seleccionar Archivo ZIP</p>
                        <p className="text-[10px] uppercase font-black tracking-widest text-white/40">Soporte para estructuras SCORM complejas</p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".zip"
                    className="hidden"
                    onChange={e => { setUnitsFile(e.target.files[0] || null); setUploadResult(null); }}
                  />
                </label>

                <div className="flex gap-3 h-full">
                  {unitsFile && (
                    <button onClick={() => { setUnitsFile(null); setUploadResult(null); }}
                      className="p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/40 text-white/50 transition-colors flex items-center justify-center flex-shrink-0">
                      <X className="w-5 h-5" />
                    </button>
                  )}

                  <button
                    onClick={uploadScormWithUnits}
                    disabled={uploadingUnits || !unitsFile}
                    className="flex-1 lg:flex-none lg:px-8 py-5 btn-neon-magenta rounded-xl font-black text-[11px] uppercase tracking-widest disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    {uploadingUnits ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Compilando...</>
                    ) : (
                      <><Upload className="w-4 h-4" /> Iniciar Inyección</>
                    )}
                  </button>
                </div>
              </div>

              {/* Barra de progreso mientras sube */}
              {uploadingUnits && (
                <div className="flex items-center gap-4 px-6 py-4 bg-[#FF00FF]/10 border border-[#FF00FF]/30 rounded-xl shadow-[inset_0_0_15px_rgba(255,0,255,0.1)]">
                  <Loader2 className="w-6 h-6 text-[#FF00FF] animate-spin flex-shrink-0" />
                  <div>
                    <p className="text-[12px] font-black uppercase tracking-widest text-[#FF00FF] mb-1">Desencriptando paquete SCORM...</p>
                    <p className="text-[11px] font-medium text-white/60">
                      Extrayendo datos de sub-unidades y sintetizando evaluaciones. Tiempo estimado: 1-3 minutos.
                    </p>
                  </div>
                </div>
              )}

              {/* Resultado exitoso */}
              {uploadResult && !uploadingUnits && (
                <div className="bg-[#ccff00]/5 border border-[#ccff00]/30 rounded-xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-[#ccff00]/20 flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-[#ccff00] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-black text-[#ccff00] text-[13px] uppercase tracking-wider mb-1 truncate">
                        Inyección Exitosa: "{uploadResult.parent_module_title}"
                      </p>
                      <div className="flex flex-wrap items-center gap-4 text-[10px] font-black uppercase tracking-widest text-white/70">
                        <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-[#ccff00]" /> {uploadResult.submodules_created} Nodos</span>
                        <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#ccff00]" /> {uploadResult.tests_created} Evals</span>
                      </div>
                    </div>
                  </div>
                  
                  {uploadResult.submodules?.length > 0 && (
                    <div className="p-6 bg-black/40">
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {uploadResult.submodules.map(s => (
                          <div key={s.id} className="flex items-center gap-2.5 px-3 py-2 bg-white/5 border border-white/10 rounded-lg min-w-0">
                            <span className="w-5 h-5 rounded bg-[#FF00FF]/20 text-[#FF00FF] text-[10px] font-black flex items-center justify-center flex-shrink-0">
                              {s.unit_number}
                            </span>
                            <span className="truncate text-white/70 text-[11px] font-bold">{s.title}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3 mt-6 pt-5 border-t border-white/10">
                        <button onClick={() => navigate(`/admin/modules/${uploadResult.parent_module_id}/tests`)}
                          className="flex items-center gap-2 px-5 py-2.5 bg-white/5 hover:bg-[#FF00FF]/20 border border-white/10 hover:border-[#FF00FF]/40 text-white/60 hover:text-[#FF00FF] rounded-xl text-[10px] font-black uppercase tracking-widest transition-all">
                          <FileText className="w-4 h-4" /> Verificar Tests
                        </button>
                        <button onClick={() => { handleAssignModule(allModules.find(m => m.id === uploadResult.parent_module_id) || { id: uploadResult.parent_module_id, title: uploadResult.parent_module_title }); }}
                          className="flex items-center gap-2 px-5 py-2.5 btn-neon-cyan rounded-xl text-[10px] font-black uppercase tracking-widest">
                          <Send className="w-4 h-4" /> Distribuir
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Tabs & Stats ── */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-end mb-6">
          <div className="lg:col-span-3">
            {filterOrg ? (
              <div className="flex flex-wrap gap-4 border-b border-white/10 pb-0">
                <button onClick={() => setView('assigned')}
                  className={`px-6 py-4 text-[11px] font-black uppercase tracking-widest border-b-2 transition-colors flex items-center gap-2 ${
                    view === 'assigned' ? 'border-[#00FFFF] text-[#00FFFF]' : 'border-transparent text-white/40 hover:text-white'
                  }`}>
                  Asignados <span className="opacity-50 hidden sm:inline">({filterOrgName})</span>
                  <span className={`px-2 py-0.5 rounded-md text-[10px] ${view === 'assigned' ? 'bg-[#00FFFF]/20' : 'bg-white/10'}`}>
                    {loadingOrgModules ? '…' : orgModules.length}
                  </span>
                </button>
                <button onClick={() => setView('all')}
                  className={`px-6 py-4 text-[11px] font-black uppercase tracking-widest border-b-2 transition-colors flex items-center gap-2 ${
                    view === 'all' ? 'border-[#00FFFF] text-[#00FFFF]' : 'border-transparent text-white/40 hover:text-white'
                  }`}>
                  Repositorio Global
                  <span className={`px-2 py-0.5 rounded-md text-[10px] ${view === 'all' ? 'bg-[#00FFFF]/20' : 'bg-white/10'}`}>
                    {allModules.length}
                  </span>
                </button>
              </div>
            ) : (
              <h2 className="text-xl font-black text-white px-2">Repositorio Global de Módulos ({allModules.length})</h2>
            )}
          </div>
          
          <div className="lg:col-span-1 flex gap-4 lg:justify-end px-2 lg:px-0">
             <div className="flex items-center gap-3">
               <span className="p-2 bg-[#00FFFF]/10 text-[#00FFFF] border border-[#00FFFF]/30 rounded-lg"><Building2 className="w-4 h-4"/></span>
               <div>
                 <p className="text-[9px] font-black uppercase tracking-widest text-white/40">Redes</p>
                 <p className="text-lg font-black">{organizations.length}</p>
               </div>
             </div>
             <div className="flex items-center gap-3 border-l border-white/10 pl-4">
               <span className="p-2 bg-[#ccff00]/10 text-[#ccff00] border border-[#ccff00]/30 rounded-lg"><CheckCircle2 className="w-4 h-4"/></span>
               <div>
                 <p className="text-[9px] font-black uppercase tracking-widest text-white/40">Activas</p>
                 <p className="text-lg font-black">{organizations.filter(o => o.active).length}</p>
               </div>
             </div>
          </div>
        </div>

        {/* ── Lista de módulos ── */}
        <div className="glass-panel overflow-hidden">
          {loadingOrgModules && view === 'assigned' ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <Loader2 className="w-10 h-10 animate-spin text-[#00FFFF] drop-shadow-[0_0_15px_#00FFFF]" />
              <p className="text-[10px] font-black uppercase tracking-widest text-[#00FFFF]">Consultando Entorno...</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {displayedModules.length === 0 ? (
                <div className="p-20 text-center flex flex-col items-center justify-center">
                  <FileText className="w-14 h-14 text-white/20 mb-4" />
                  {view === 'assigned' && filterOrg ? (
                    <>
                      <p className="text-lg font-black text-white mb-2">Sin asignaciones operativas</p>
                      <p className="text-sm text-white/50 mb-6 max-w-md">Esta corporación no tiene módulos desplegados en su entorno. Cambia a la vista global para distribuir cargas.</p>
                      <button onClick={() => setView('all')} className="btn-neon-cyan px-8 py-3 rounded-xl text-xs uppercase tracking-widest font-black">
                        Abrir Repositorio Global
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-lg font-black text-white mb-2">Repositorio Vacío</p>
                      <p className="text-sm text-white/50 mb-6">No existen módulos registrados en el sistema central.</p>
                      <button className="btn-neon-magenta px-8 py-3 rounded-xl text-xs uppercase tracking-widest font-black flex items-center gap-2" onClick={() => { setShowUploadPanel(true); window.scrollTo(0,0); }}>
                        <Upload className="w-4 h-4" /> Inyectar SCORM
                      </button>
                    </>
                  )}
                </div>
              ) : (
                displayedModules.map((module) => {
                  const alreadyAssigned = filterOrg && assignedIds.has(module.id);
                  return (
                    <div key={module.id} className="p-4 sm:p-6 hover:bg-white/[0.02] transition-colors group border-b border-white/5 last:border-0">
                      
                      {/* Fila 1: Título + badges */}
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <h3 className="text-sm sm:text-base font-black text-white group-hover:text-[#00FFFF] transition-colors mr-2">{module.title}</h3>
                        <div className="flex flex-wrap gap-2">
                          <span className="px-2 py-0.5 bg-white/5 border border-white/10 text-white/60 text-[9px] uppercase font-black tracking-widest rounded-md">
                            {module.difficulty === 'beginner' ? 'Básico' : module.difficulty === 'intermediate' ? 'Intermedio' : module.difficulty === 'advanced' ? 'Avanzado' : module.difficulty}
                          </span>
                          <span className="px-2 py-0.5 bg-[#00FFFF]/10 border border-[#00FFFF]/30 text-[#00FFFF] text-[9px] uppercase font-black tracking-widest rounded-md">{module.category}</span>
                          {module.has_submodules && <span className="px-2 py-0.5 bg-[#FF00FF]/10 border border-[#FF00FF]/30 text-[#FF00FF] text-[9px] uppercase font-black tracking-widest rounded-md flex items-center gap-1"><PackageOpen className="w-3 h-3" />{module.submodule_count || '?'} Nodos</span>}
                          {module.requires_screen_record && <span className="px-2 py-0.5 bg-[#FF00FF]/10 border border-[#FF00FF]/30 text-[#FF00FF] text-[9px] uppercase font-black tracking-widest rounded-md flex items-center gap-1"><Video className="w-3 h-3" /> Supervisado</span>}
                          {module.hours > 0 && <span className="px-2 py-0.5 bg-white/10 border border-white/20 text-white/70 text-[9px] uppercase font-black tracking-widest rounded-md flex items-center gap-1"><Clock className="w-3 h-3" /> {module.hours}h</span>}
                          {alreadyAssigned && <span className="px-2 py-0.5 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] text-[9px] uppercase font-black tracking-widest rounded-md flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Distribuido</span>}
                          {module.organization_name && !filterOrg && <span className="px-2 py-0.5 bg-white/10 border border-white/20 text-white/70 text-[9px] uppercase font-black tracking-widest rounded-md flex items-center gap-1"><Building2 className="w-3 h-3" /> {module.organization_name}</span>}
                        </div>
                      </div>

                      {/* Fila 2: Descripción */}
                      <p className="text-[11px] sm:text-[12px] text-white/50 mb-4 line-clamp-2 leading-relaxed max-w-3xl">
                        {module.description || 'Sin datos descriptivos disponibles para este módulo.'}
                      </p>

                      {/* Fila 3: Meta + Botones */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-start gap-8">
                        <div className="flex flex-wrap items-center gap-4 text-[9px] font-black uppercase tracking-widest text-white/30">
                          <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> {module.videos?.length || 0} Media</span>
                          <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {module.skills?.length || 0} Skills</span>
                          <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(module.created_at).toLocaleDateString()}</span>
                        </div>

                        {/* Botones de acción */}
                        <div className="flex flex-wrap items-center gap-2 flex-shrink-0 ml-0">
                          <button
                            onClick={() => navigate(`/admin/modules/${module.id}/tests`)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-[#FF00FF]/20 border border-white/10 hover:border-[#FF00FF]/40 text-white/60 hover:text-[#FF00FF] rounded-lg text-[9px] font-black uppercase tracking-widest transition-all">
                            <FileText className="w-3.5 h-3.5" /> Eval.
                          </button>
                          {!alreadyAssigned && (
                            <button onClick={() => handleAssignModule(module)} className="flex items-center gap-1.5 px-3 py-2 btn-neon-cyan rounded-lg text-[9px] font-black uppercase tracking-widest">
                              <Send className="w-3.5 h-3.5" /> {alreadyAssigned ? 'Asignar más' : 'Asignar'}
                            </button>
                          )}
                          <button
  onClick={() => openRename(module.id, module.title, 'module')}
  className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-[#00FFFF]/20 border border-white/10 hover:border-[#00FFFF]/40 text-white/60 hover:text-[#00FFFF] rounded-lg text-[9px] font-black uppercase tracking-widest transition-all">
  <Edit className="w-3.5 h-3.5" /> Renombrar
</button>
                          <button
                            onClick={() => openConfig(module)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-[#ccff00]/20 border border-white/10 hover:border-[#ccff00]/40 text-white/60 hover:text-[#ccff00] rounded-lg text-[9px] font-black uppercase tracking-widest transition-all">
                            <Settings className="w-3.5 h-3.5" /> Config.
                          </button>
                          <button
                            disabled={deletingId === module.id}
                            onClick={() => deleteModule(module.id, module.title)}
                            className="flex items-center justify-center p-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 hover:border-red-500/50 text-red-400 rounded-lg transition-all disabled:opacity-50">
                            {deletingId === module.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>

                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

      {/* ── Modal de Configuración (Grabación & Horas) ── */}
      {showConfigModal && configModule && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-md">
          <div className="bg-[#060810] rounded-3xl w-full max-w-md max-h-[90vh] flex flex-col shadow-[0_0_50px_rgba(204,255,0,0.15)] border border-[#ccff00]/30">
            <div className="flex items-center justify-between p-6 border-b border-white/10 bg-gradient-to-r from-[#ccff00]/10 to-transparent">
              <div className="min-w-0">
                <h2 className="text-xl font-black text-white tracking-tight">Configuración</h2>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#ccff00] mt-1 truncate">{configModule.title}</p>
              </div>
              <button onClick={closeConfigModal} className="text-white/40 hover:text-white p-2 bg-white/5 hover:bg-white/10 rounded-xl transition-colors flex-shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto">
              {/* Toggle de Grabación de Pantalla */}
              <div className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`p-2 rounded-lg flex-shrink-0 ${configData.requires_screen_record ? 'bg-[#FF00FF]/20 text-[#FF00FF] border border-[#FF00FF]/30' : 'bg-white/10 text-white/40 border border-white/10'}`}>
                    <Video className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[12px] font-black uppercase tracking-widest text-white truncate">Supervisión Video</p>
                    <p className="text-[10px] text-white/50 truncate">Exigir grabación en evaluaciones</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-4">
                  <input type="checkbox" className="sr-only peer" checked={configData.requires_screen_record} onChange={e => setConfigData(prev => ({...prev, requires_screen_record: e.target.checked}))} />
                  <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF00FF]"></div>
                </label>
              </div>

              {/* Input de Horas */}
              <div>
                <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-[#ccff00] mb-3">
                  <Clock className="w-3.5 h-3.5" /> Horas del Módulo
                </label>
                <input 
                  type="number" 
                  min="0"
                  className="cy-input text-white/80"
                  value={configData.hours}
                  onChange={e => setConfigData(prev => ({ ...prev, hours: Number(e.target.value) }))} 
                  placeholder="Ej: 40"
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 p-6 border-t border-white/10 bg-black/40">
              <button onClick={closeConfigModal} className="px-6 py-3 border border-white/20 text-white rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-white/5 transition-colors">
                Cancelar
              </button>
              <button onClick={submitConfig} disabled={configuring} className="btn-neon-lime px-8 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50">
                {configuring ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</> : 'Guardar Datos'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal asignación ── */}
      {showAssignModal && selectedModule && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-md">
          <div className="bg-[#060810] rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-[0_0_50px_rgba(0,255,255,0.15)] border border-[#00FFFF]/30">
            <div className="flex items-center justify-between p-6 md:p-8 border-b border-white/10 bg-gradient-to-r from-[#00FFFF]/10 to-transparent">
              <div className="min-w-0">
                <h2 className="text-xl font-black text-white tracking-tight">Distribución de Carga</h2>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#00FFFF] mt-1 truncate">{selectedModule.title}</p>
              </div>
              <button onClick={closeModal} className="text-white/40 hover:text-white p-2 bg-white/5 hover:bg-white/10 rounded-xl transition-colors flex-shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6">
              
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-3">Corporación Destino <span className="text-red-500">*</span></label>
                <select className="cy-input appearance-none"
                  value={selectedOrg} onChange={e => setSelectedOrg(e.target.value)}>
                  <option value="">Selecciona una entidad en la red</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name} ({org.current_users} activos)
                    </option>
                  ))}
                </select>
              </div>

              {selectedOrg && (
                <div className="space-y-6 animate-fadeIn">
                  
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-3">Alcance de Distribución <span className="text-red-500">*</span></label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[
                        { value: 'all',        label: 'Red Completa' },
                        { value: 'department', label: 'Por Sectores' },
                        { value: 'users',      label: 'Operarios Específicos' },
                      ].map(opt => (
                        <label key={opt.value}
                          className={`flex flex-col items-center justify-center gap-2 p-4 border rounded-xl cursor-pointer transition-all text-center ${
                            assignmentData.assignTo === opt.value
                              ? 'bg-[#00FFFF]/10 border-[#00FFFF] text-[#00FFFF] shadow-[inset_0_0_15px_rgba(0,255,255,0.1)]'
                              : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white'
                          }`}>
                          <input type="radio" name="assignTo" value={opt.value} className="hidden"
                            checked={assignmentData.assignTo === opt.value}
                            onChange={() => setAssignmentData(prev => ({
                              ...prev, assignTo: opt.value, departmentIds: [], userIds: []
                            }))} />
                          <span className="text-[11px] font-black uppercase tracking-widest leading-tight">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                      
                  {assignmentData.assignTo === 'department' && (
                    <div className="animate-fadeIn">
                      <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-3">Sectores Disponibles</label>
                      <div className="max-h-48 overflow-y-auto border border-white/10 rounded-xl p-2 bg-black/40 space-y-1 chat-scroll">
                        {departments.length === 0
                          ? <p className="text-[11px] font-bold uppercase tracking-widest text-white/30 p-4 text-center">Sin sectores detectados.</p>
                          : departments.map(dept => (
                            <label key={dept.id} className="flex items-center gap-3 p-3 hover:bg-white/5 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-white/10">
                              <input type="checkbox" className="w-4 h-4 rounded border-white/20 bg-black/50 accent-[#00FFFF]"
                                checked={assignmentData.departmentIds.includes(dept.id)}
                                onChange={() => toggleId('departmentIds', dept.id)} />
                              <span className="text-[13px] font-bold text-white flex-1">{dept.name}</span>
                              <span className="text-[9px] font-black uppercase tracking-widest text-white/40 bg-white/5 px-2 py-1 rounded">{dept.user_count} OPs</span>
                            </label>
                          ))
                        }
                      </div>
                    </div>
                  )}

                  {assignmentData.assignTo === 'users' && (
                    <div className="animate-fadeIn">
                      <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-3">Operarios Disponibles</label>
                      <div className="max-h-48 overflow-y-auto border border-white/10 rounded-xl p-2 bg-black/40 space-y-1 chat-scroll">
                        {users.length === 0
                          ? <p className="text-[11px] font-bold uppercase tracking-widest text-white/30 p-4 text-center">Sin operarios detectados.</p>
                          : users.map(u => (
                            <label key={u.id} className="flex items-center gap-3 p-3 hover:bg-white/5 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-white/10">
                              <input type="checkbox" className="w-4 h-4 rounded border-white/20 bg-black/50 accent-[#00FFFF]"
                                checked={assignmentData.userIds.includes(u.id)}
                                onChange={() => toggleId('userIds', u.id)} />
                              <div className="flex-1 min-w-0">
                                <p className="text-[13px] font-bold text-white truncate">{u.name}</p>
                                <p className="text-[10px] font-medium text-white/40 truncate">{u.email}</p>
                              </div>
                            </label>
                          ))
                        }
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-3">Límite Temporal <span className="text-white/40">(Opcional)</span></label>
                      <input type="date" className="cy-input text-white/80"
                        value={assignmentData.dueDate}
                        onChange={e => setAssignmentData(prev => ({ ...prev, dueDate: e.target.value }))} />
                    </div>
                    
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${assignmentData.mandatory ? 'bg-[#00FFFF] border-[#00FFFF]' : 'bg-black/50 border-white/20 group-hover:border-[#00FFFF]/50'}`}>
                          {assignmentData.mandatory && <Check className="w-3.5 h-3.5 text-black" />}
                        </div>
                        <input type="checkbox" className="hidden" checked={assignmentData.mandatory}
                          onChange={e => setAssignmentData(prev => ({ ...prev, mandatory: e.target.checked }))} />
                        <span className="text-[11px] font-black uppercase tracking-widest text-white/70 group-hover:text-white transition-colors">Directiva Obligatoria</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-4 p-6 md:p-8 border-t border-white/10 bg-black/40">
              <button onClick={closeModal} className="px-6 py-3.5 border border-white/20 text-white rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-white/5 transition-colors">
                Abortar
              </button>
              <button onClick={submitAssignment} disabled={!selectedOrg || assigning}
                className="btn-neon-cyan px-8 py-3.5 rounded-xl text-[11px] font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50">
                {assigning ? <><Loader2 className="w-4 h-4 animate-spin" /> Procesando...</> : <><Send className="w-4 h-4" /> Ejecutar Distribución</>}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
    {showRenameModal && (
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-md">
    <div className="bg-[#060810] rounded-3xl w-full max-w-md shadow-[0_0_50px_rgba(0,255,255,0.15)] border border-[#00FFFF]/30">
      <div className="flex items-center justify-between p-6 border-b border-white/10 bg-gradient-to-r from-[#00FFFF]/10 to-transparent">
        <div>
          <h2 className="text-xl font-black text-white">Renombrar Módulo</h2>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#00FFFF] mt-1 truncate">{renameTarget.title}</p>
        </div>
        <button onClick={() => setShowRenameModal(false)} className="text-white/40 hover:text-white p-2 bg-white/5 hover:bg-white/10 rounded-xl transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-6">
        <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-3">Nuevo nombre *</label>
        <input
          className="cy-input"
          value={renameValue}
          onChange={e => setRenameValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && saveRename()}
          autoFocus
        />
      </div>
      <div className="flex justify-end gap-4 p-6 border-t border-white/10 bg-black/40">
        <button onClick={() => setShowRenameModal(false)} className="px-6 py-3 border border-white/20 text-white rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-white/5 transition-colors">
          Cancelar
        </button>
        <button onClick={saveRename} disabled={!renameValue.trim() || renameSaving}
          className="btn-neon-cyan px-8 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-50">
          {renameSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</> : <><Edit className="w-4 h-4" /> Guardar</>}
        </button>
      </div>
    </div>
  </div>
)}
  </MainLayout>
);
};

export default SuperAdminModules;
